import React, { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useProcurement } from '@/context/ProcurementContext';
import { 
  DebitNotesList, 
  DebitNotesForm, 
  DebitNotesDetails 
} from '@/components/debitnotes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Receipt, 
  Plus, 
  Download,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { DebitNote } from '@/data/mockData';

const DebitNotes = () => {
  const { state } = useProcurement();
  const [debitNotes, setDebitNotes] = useState<DebitNote[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDebitNote, setSelectedDebitNote] = useState<DebitNote | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDebitNotes();
  }, []);

  const loadDebitNotes = () => {
    try {
      const storedDebitNotes = localStorage.getItem('DEBIT_NOTES');
      if (storedDebitNotes) {
        const parsedNotes = JSON.parse(storedDebitNotes);
        setDebitNotes(parsedNotes);
      } else {
        // Initialize with mock data if no stored data
        setDebitNotes([]);
      }
    } catch (error) {
      console.error('Error loading debit notes:', error);
      setDebitNotes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveDebitNotes = (newDebitNotes: DebitNote[]) => {
    try {
      localStorage.setItem('DEBIT_NOTES', JSON.stringify(newDebitNotes));
      setDebitNotes(newDebitNotes);
    } catch (error) {
      console.error('Error saving debit notes:', error);
      toast.error('Failed to save debit notes');
    }
  };

  const handleCreateDebitNote = async (debitNoteData: Partial<DebitNote>): Promise<boolean> => {
    try {
      const newDebitNote: DebitNote = {
        id: Date.now(),
        debitNoteNumber: debitNoteData.debitNoteNumber || `DN-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
        linkedPOId: debitNoteData.linkedPOId,
        linkedInvoiceId: debitNoteData.linkedInvoiceId,
        linkedPONumber: debitNoteData.linkedPONumber,
        linkedInvoiceNumber: debitNoteData.linkedInvoiceNumber,
        vendorId: debitNoteData.vendorId || 0,
        vendorName: debitNoteData.vendorName || 'Unknown Vendor',
        reason: debitNoteData.reason || '',
        items: debitNoteData.items || [],
        totalAmount: debitNoteData.totalAmount || 0,
        status: 'Open',
        createdAt: debitNoteData.createdAt || new Date().toISOString(),
        createdBy: debitNoteData.createdBy || 1,
        createdByName: debitNoteData.createdByName || 'System User',
        notes: debitNoteData.notes,
        attachments: debitNoteData.attachments
      };

      const updatedDebitNotes = [...debitNotes, newDebitNote];
      saveDebitNotes(updatedDebitNotes);
      return true;
    } catch (error) {
      console.error('Error creating debit note:', error);
      return false;
    }
  };

  const handleViewDebitNote = (debitNote: DebitNote) => {
    setSelectedDebitNote(debitNote);
    setShowDetails(true);
  };

  const handleSendToVendor = async (debitNote: DebitNote) => {
    try {
      const updatedDebitNotes = debitNotes.map(dn => 
        dn.id === debitNote.id 
          ? { ...dn, status: 'Sent to Vendor' as const, sentToVendorAt: new Date().toISOString() }
          : dn
      );
      
      saveDebitNotes(updatedDebitNotes);
      toast.success('Debit note sent to vendor successfully');
      
      // Update selected debit note if it's currently being viewed
      if (selectedDebitNote?.id === debitNote.id) {
        setSelectedDebitNote(updatedDebitNotes.find(dn => dn.id === debitNote.id) || null);
      }
    } catch (error) {
      console.error('Error sending debit note to vendor:', error);
      toast.error('Failed to send debit note to vendor');
    }
  };

  const handleDownloadDebitNote = (debitNote: DebitNote) => {
    // Generate a simple text file for download
    const content = `
DEBIT NOTE

Debit Note Number: ${debitNote.debitNoteNumber}
Vendor: ${debitNote.vendorName}
Date: ${new Date(debitNote.createdAt).toLocaleDateString()}
Reason: ${debitNote.reason}

Linked Document: ${debitNote.linkedPONumber || debitNote.linkedInvoiceNumber}

REJECTED ITEMS:
${debitNote.items.map(item => `
- ${item.skuName} (${item.skuCode})
  Quantity: ${item.rejectedQuantity}
  Unit Price: ${formatCurrency(item.unitPrice)}
  Total: ${formatCurrency(item.totalPrice)}
  Reason: ${item.rejectionReason}
`).join('')}

TOTAL DEBIT AMOUNT: ${formatCurrency(debitNote.totalAmount)}

Notes: ${debitNote.notes || 'None'}

Generated on: ${new Date().toLocaleString()}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debit-note-${debitNote.debitNoteNumber}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Debit note downloaded successfully');
  };

  const handleUpdateStatus = (debitNote: DebitNote, status: DebitNote['status']) => {
    try {
      const updatedDebitNotes = debitNotes.map(dn => 
        dn.id === debitNote.id 
          ? { 
              ...dn, 
              status,
              closedAt: status === 'Closed' ? new Date().toISOString() : dn.closedAt
            }
          : dn
      );
      
      saveDebitNotes(updatedDebitNotes);
      toast.success(`Debit note status updated to ${status}`);
      
      // Update selected debit note if it's currently being viewed
      if (selectedDebitNote?.id === debitNote.id) {
        setSelectedDebitNote(updatedDebitNotes.find(dn => dn.id === debitNote.id) || null);
      }
    } catch (error) {
      console.error('Error updating debit note status:', error);
      toast.error('Failed to update debit note status');
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedDebitNote(null);
  };

  // Calculate summary statistics
  const totalDebitNotes = debitNotes.length;
  const openDebitNotes = debitNotes.filter(dn => dn.status === 'Open').length;
  const sentDebitNotes = debitNotes.filter(dn => dn.status === 'Sent to Vendor').length;
  const closedDebitNotes = debitNotes.filter(dn => dn.status === 'Closed').length;
  const disputedDebitNotes = debitNotes.filter(dn => dn.status === 'Disputed').length;
  const totalDebitAmount = debitNotes.reduce((sum, dn) => sum + dn.totalAmount, 0);
  const openDebitAmount = debitNotes
    .filter(dn => dn.status === 'Open')
    .reduce((sum, dn) => sum + dn.totalAmount, 0);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
            <p className="text-muted-foreground">Loading debit notes...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Debit Notes</h1>
            <p className="text-muted-foreground">Manage debit notes for rejected items and invoices</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export All
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Receipt className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Debit Notes</p>
                  <p className="text-2xl font-bold">{totalDebitNotes}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Open</p>
                  <p className="text-2xl font-bold">{openDebitNotes}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FileText className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sent to Vendor</p>
                  <p className="text-2xl font-bold">{sentDebitNotes}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <DollarSign className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Debit Amount</p>
                  <p className="text-2xl font-bold">{formatCurrency(totalDebitAmount)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Status Distribution</CardTitle>
            <CardDescription>
              Overview of debit notes by status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                <p className="text-2xl font-bold text-yellow-600">{openDebitNotes}</p>
                <p className="text-sm text-muted-foreground">Open</p>
                <Badge variant="secondary" className="mt-2">Pending Action</Badge>
              </div>
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">{sentDebitNotes}</p>
                <p className="text-sm text-muted-foreground">Sent to Vendor</p>
                <Badge variant="default" className="mt-2">Awaiting Response</Badge>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <p className="text-2xl font-bold text-green-600">{closedDebitNotes}</p>
                <p className="text-sm text-muted-foreground">Closed</p>
                <Badge variant="outline" className="mt-2">Resolved</Badge>
              </div>
              <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                <p className="text-2xl font-bold text-red-600">{disputedDebitNotes}</p>
                <p className="text-sm text-muted-foreground">Disputed</p>
                <Badge variant="destructive" className="mt-2">Under Review</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Debit Notes List */}
        <DebitNotesList
          debitNotes={debitNotes}
          onView={handleViewDebitNote}
          onSendToVendor={handleSendToVendor}
          onDownload={handleDownloadDebitNote}
          onUpdateStatus={handleUpdateStatus}
        />

        {/* Debit Note Form Modal */}
        {showForm && (
          <DebitNotesForm
            isOpen={showForm}
            onClose={handleCloseForm}
            onSave={handleCreateDebitNote}
            mode="po"
          />
        )}

        {/* Debit Note Details Modal */}
        {showDetails && selectedDebitNote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-background rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <DebitNotesDetails
                  debitNote={selectedDebitNote}
                  onClose={handleCloseDetails}
                  onSendToVendor={handleSendToVendor}
                  onDownload={handleDownloadDebitNote}
                  onUpdateStatus={handleUpdateStatus}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default DebitNotes;
